import { IconeTrait } from '@/components/ui/icone-trait';
import { renderLienRouteur } from '@/components/ui/lien-routeur';
import { Button, Card, Icon } from '@pms-digital/design-system';
import type { ResumeTraduction } from '@rosetta/shared';
import type { MouseEvent } from 'react';
import { formaterDate } from './formater-date';
import { IconeHistorique } from './icone-historique';

interface CarteEchecProps {
  traduction: ResumeTraduction;
  /** The current instant, to word the date ("aujourd'hui"); injectable for the tests. */
  maintenant?: Date;
  /** Opens the input screen filled with the title, the text and the noyaux. */
  onRelancer: () => void;
  /** Removes the card at once; the choice is remembered in this browser. */
  onEcarter: (evenement: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * H5 (#5) and H7 (#7): the card of a translation that did not succeed, or that was interrupted,
 * the `Card` of the design system, with no colour of a noyau: the title with the date under it,
 * "n'a pas abouti" in `rouge-deep` with the `error` icon (H5) or "interrompue" in `gray-700` with
 * the `stop_circle` icon (H7) at the top right, two lines of the original text and, in the foot,
 * "relancer" and "écarter". Since #13 the title of H5 is a link, stretched over the whole card by
 * the package (#21, the buttons are above it): it opens the page of the failure, at the address of
 * the result (the message of Tahiti, and the translations a partial launch did produce); H7 opens
 * nothing.
 */
export function CarteEchec({ traduction, maintenant, onRelancer, onEcarter }: CarteEchecProps) {
  const { id, titre, texteOrigine, creeLe, statut } = traduction;
  const interrompue = statut === 'interrompue';
  const titreAffiche =
    titre === undefined ? (
      <span className="font-normal text-gray-600 italic">Sans titre</span>
    ) : (
      titre
    );
  return (
    <Card
      surface="border"
      label={`${titre ?? 'Sans titre'}, traduction ${interrompue ? 'interrompue' : 'échouée'}`}
      title={titreAffiche}
      {...(interrompue ? {} : { titleHref: `/resultat/${id}`, renderLink: renderLienRouteur })}
      meta={<time dateTime={creeLe}>{formaterDate(creeLe, maintenant)}</time>}
      trailing={
        interrompue ? (
          <span className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-caption text-gray-700">
            <IconeTrait name="stop_circle" size={16} />
            interrompue
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-caption text-rouge-deep">
            <Icon name="error" size={16} />
            n'a pas abouti
          </span>
        )
      }
      actions={
        <>
          <Button type="button" variant="ghost" size="sm" onClick={onRelancer}>
            <IconeHistorique name="refresh" size={18} />
            relancer
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onEcarter}>
            <Icon name="visibility_off" size={20} />
            écarter
          </Button>
        </>
      }
    >
      <p className="m-0 line-clamp-2 min-h-14 font-light leading-[1.55]">{texteOrigine}</p>
    </Card>
  );
}
